// src/pages/ChooseAction.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown, ChevronRight, MapPin } from "lucide-react";

import CharacterSvgUrl from "../assets/characters/choose-action/undraw_newsletter-subscriber_plsr.svg";
import { useAuth } from "../context/auth-context";

type MenuItemType = "route" | "function";

type ModulePermissions = {
  canView?: boolean | null;
  canCreate?: boolean | null;
  canUpdate?: boolean | null;
  canDelete?: boolean | null;
  canReport?: boolean | null;
};

type SubItem = {
  label: string;
  codigo?: string;
  tipoItem?: MenuItemType;
  tipo_item?: MenuItemType;
  actionKey?: string | null;
  action_key?: string | null;
  routeEnabled?: boolean;
  route_enabled?: boolean;
  developed?: boolean;
  devOnly?: boolean;
  children?: SubItem[];
};

type MainItem = {
  id?: number | string | null;
  label: string;
  codigo?: string;
  subitems: SubItem[];
  themeColor?: string;
  developed?: boolean;
  devOnly?: boolean;
  permissions?: ModulePermissions | null;
};

type Department = {
  id?: number | string | null;
  name: string;
  slug?: string;
  routeKey?: string;
  themeColor?: string;
  iconKey?: string;
  ordem?: number | null;
  items: MainItem[];
};

type AddressOption = {
  value: string;
  label: string;
  lines: string[];
  isAll?: boolean;
};

type SessaoEndereco = {
  id?: number | string | null;
  label?: string | null;
  nome?: string | null;
  logradouro?: string | null;
  numero?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  uf?: string | null;
};

type SessaoEnderecoComModulos = SessaoEndereco & {
  departamentos_acessiveis?: any[];
};

const ALL_ADDRESSES_VALUE = "__ALL__";
const STORAGE_KEY_SELECTED_ADDRESS = "eva:leftbar:selected-address-global";

const MAIN_THAT_GO_TO_FIRST_SUB = new Set(["saidas", "saídas"]);

const removeAccents = (s: string) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const norm = (s: string) => removeAccents(String(s)).toLowerCase();

const slugify = (s: string) =>
  norm(s)
    .replace(/&/g, "e")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();

const getDeptPermKey = (d: Department) =>
  d.slug ? String(d.slug) : slugify(d.name);

const getDeptRouteKey = (d: Department) =>
  d.routeKey ? String(d.routeKey) : d.slug ? String(d.slug) : slugify(d.name);

const getMainSlug = (item: MainItem) => slugify(item.label);

const cleanCodigoForRoute = (dept: Department, codigo?: string) => {
  const raw = String(codigo || "").trim();
  if (!raw) return "";

  const deptKey = slugify(getDeptRouteKey(dept));
  const parts = slugify(raw).split("-").filter(Boolean);

  if (!parts.length) return "";
  if (parts[0] === deptKey) parts.shift();
  if (parts[parts.length - 1] === deptKey) parts.pop();

  return parts.join("-");
};

const getSubSlug = (dept: Department, sub: SubItem) => {
  const cleaned = cleanCodigoForRoute(dept, sub.codigo);
  return cleaned || slugify(sub.label);
};

const isFunctionItem = (sub: SubItem) => {
  const tipoItem = sub.tipoItem || sub.tipo_item;
  const routeEnabled =
    sub.routeEnabled !== undefined
      ? sub.routeEnabled
      : sub.route_enabled !== undefined
      ? sub.route_enabled
      : true;

  return tipoItem === "function" || routeEnabled === false;
};

const isGenerateReport = (sub: SubItem) => {
  const actionKey = sub.actionKey || sub.action_key || "";
  const label = norm(sub.label || "");
  const codigo = norm(sub.codigo || "");

  return (
    actionKey === "generate_report" ||
    codigo === "gerar-relatorio" ||
    (label.includes("gerar") && label.includes("relatorio"))
  );
};

const shouldHideFunctionItem = (sub: SubItem) =>
  isFunctionItem(sub) || isGenerateReport(sub);

const mapPermissions = (mod: any): ModulePermissions | null => {
  const p = mod?.permissions;

  if (!p || typeof p !== "object") return null;

  return {
    canView:
      p.canView !== undefined && p.canView !== null ? p.canView === true : null,
    canCreate:
      p.canCreate !== undefined && p.canCreate !== null
        ? p.canCreate === true
        : null,
    canUpdate:
      p.canUpdate !== undefined && p.canUpdate !== null
        ? p.canUpdate === true
        : null,
    canDelete:
      p.canDelete !== undefined && p.canDelete !== null
        ? p.canDelete === true
        : null,
    canReport:
      p.canReport !== undefined && p.canReport !== null
        ? p.canReport === true
        : null,
  };
};

const mapSubItemRecursive = (sub: any): SubItem => ({
  label: String(sub?.label || ""),
  codigo: String(sub?.codigo || ""),
  tipoItem:
    sub?.tipoItem === "function" || sub?.tipo_item === "function"
      ? "function"
      : "route",
  actionKey:
    sub?.actionKey !== undefined && sub?.actionKey !== null
      ? String(sub.actionKey)
      : sub?.action_key !== undefined && sub?.action_key !== null
      ? String(sub.action_key)
      : null,
  routeEnabled:
    sub?.routeEnabled !== undefined
      ? sub.routeEnabled !== false
      : sub?.route_enabled !== undefined
      ? sub.route_enabled !== false
      : true,
  developed: sub?.developed === true,
  devOnly: sub?.devOnly === true || sub?.dev_only === true,
  children: Array.isArray(sub?.children)
    ? sub.children.map((child: any) => mapSubItemRecursive(child))
    : [],
});

const mapDepartmentFromApi = (dept: any): Department => ({
  id: dept?.id ?? null,
  name: String(dept?.nome || ""),
  slug: String(dept?.slug || ""),
  routeKey:
    dept?.routeKey !== undefined && dept?.routeKey !== null
      ? String(dept.routeKey)
      : dept?.route_key !== undefined && dept?.route_key !== null
      ? String(dept.route_key)
      : String(dept?.slug || ""),
  themeColor:
    dept?.themeColor !== undefined && dept?.themeColor !== null
      ? String(dept.themeColor)
      : dept?.theme_color !== undefined && dept?.theme_color !== null
      ? String(dept.theme_color)
      : "#0f333b",
  iconKey:
    dept?.iconKey !== undefined && dept?.iconKey !== null
      ? String(dept.iconKey)
      : dept?.icon_key !== undefined && dept?.icon_key !== null
      ? String(dept.icon_key)
      : "lucide:circle",
  ordem:
    dept?.ordem !== undefined && dept?.ordem !== null
      ? Number(dept.ordem)
      : null,
  items: Array.isArray(dept?.modulos)
    ? dept.modulos
        .filter((mod: any) => {
          const permissions = mapPermissions(mod);
          return permissions?.canView === false ? false : true;
        })
        .map((mod: any) => ({
          id: mod?.id ?? null,
          label: String(mod?.label || ""),
          codigo: String(mod?.codigo || ""),
          developed: mod?.developed === true,
          devOnly: mod?.devOnly === true || mod?.dev_only === true,
          permissions: mapPermissions(mod),
          themeColor:
            mod?.themeColor !== undefined && mod?.themeColor !== null
              ? String(mod.themeColor)
              : mod?.theme_color !== undefined && mod?.theme_color !== null
              ? String(mod.theme_color)
              : dept?.themeColor ?? dept?.theme_color ?? "#0f333b",
          subitems: Array.isArray(mod?.subitems)
            ? mod.subitems.map((sub: any) => mapSubItemRecursive(sub))
            : [],
        }))
    : [],
});

const mapAddressOption = (end: SessaoEndereco): AddressOption => {
  const nome =
    typeof end?.nome === "string" && end.nome.trim() ? end.nome.trim() : "";

  const linhaPrincipal = [end?.logradouro, end?.numero]
    .filter((x) => typeof x === "string" && String(x).trim().length > 0)
    .map((x) => String(x).trim())
    .join(", ");

  const linhaSecundaria = [end?.bairro, end?.cidade, end?.uf]
    .filter((x) => typeof x === "string" && String(x).trim().length > 0)
    .map((x) => String(x).trim())
    .join(" - ");

  const fallbackLabel =
    typeof end?.label === "string" && end.label.trim()
      ? end.label.trim()
      : [nome, linhaPrincipal, linhaSecundaria].filter(Boolean).join(" • ") ||
        `Endereço ${String(end?.id ?? "")}`;

  const lines = [nome, linhaPrincipal, linhaSecundaria].filter(
    (x) => typeof x === "string" && x.trim().length > 0
  );

  return {
    value: String(end?.id ?? ""),
    label: fallbackLabel,
    lines: lines.length > 0 ? lines : [fallbackLabel],
  };
};

const buildSubPath = (
  dept: Department,
  item: MainItem,
  sub: SubItem,
  ancestry: SubItem[] = []
): string => {
  const baseDeptPath = `/${getDeptRouteKey(dept)}`;
  const baseMainPath = `${baseDeptPath}/${getMainSlug(item)}`;
  const chain = [...ancestry, sub]
    .map((entry) => getSubSlug(dept, entry))
    .filter(Boolean);

  return `${baseMainPath}/${chain.join("/")}`;
};

const getRenderableSubitems = (subs: SubItem[] = []): SubItem[] => {
  const walk = (nodes: SubItem[]): SubItem[] => {
    const output: SubItem[] = [];

    for (const node of nodes || []) {
      const children = Array.isArray(node.children) ? walk(node.children) : [];

      if (shouldHideFunctionItem(node)) {
        output.push(...children);
        continue;
      }

      output.push({
        ...node,
        children,
      });
    }

    return output;
  };

  return walk(subs);
};

const getFirstVisibleNestedPath = (
  dept: Department,
  item: MainItem,
  subitems: SubItem[],
  ancestry: SubItem[] = []
): string | null => {
  const renderable = getRenderableSubitems(subitems);

  for (const sub of renderable) {
    const currentPath = buildSubPath(dept, item, sub, ancestry);
    const children = getRenderableSubitems(sub.children || []);

    if (children.length > 0) {
      const deeper = getFirstVisibleNestedPath(dept, item, children, [
        ...ancestry,
        sub,
      ]);

      return deeper || currentPath;
    }

    return currentPath;
  }

  return null;
};

const getMainPath = (dept: Department, item: MainItem) => {
  const baseDeptPath = `/${getDeptRouteKey(dept)}`;
  const mainSlug = getMainSlug(item);
  const baseMainPath = `${baseDeptPath}/${mainSlug}`;
  const visibleSubs = getRenderableSubitems(item.subitems || []);

  if (
    MAIN_THAT_GO_TO_FIRST_SUB.has(norm(item.label)) &&
    visibleSubs.length > 0
  ) {
    return getFirstVisibleNestedPath(dept, item, visibleSubs) || baseMainPath;
  }

  return baseMainPath;
};

const findDepartmentFromPath = (
  pathname: string,
  departments: Department[]
): Department | null => {
  const parts = pathname.split("/").filter(Boolean);
  if (!parts.length) return null;

  const deptFromPath = parts[0];

  return (
    departments.find(
      (d) =>
        norm(getDeptRouteKey(d)) === norm(deptFromPath) ||
        norm(getDeptPermKey(d)) === norm(deptFromPath)
    ) || null
  );
};

const getSubitemPriority = (sub: SubItem) => {
  const label = norm(sub.label);
  const codigo = norm(sub.codigo || "");

  if (codigo === "estoque" || label === "estoque") return 1;
  if (codigo.includes("produtos") || label.includes("produtos")) return 2;
  if (codigo.includes("entradas") || label.includes("entradas")) return 3;
  if (codigo.includes("saidas") || label.includes("saídas")) return 4;
  if (codigo.includes("standby") || label.includes("standby")) return 5;
  if (
    codigo.includes("realizar-movimentacao") ||
    label.includes("realizar movimentacao") ||
    label.includes("realizar movimentação")
  ) {
    return 6;
  }
  if (label.includes("registrar") || label.includes("adicionar")) return 7;

  return 99;
};

const shouldShowSubitem = (sub: SubItem) => {
  if (shouldHideFunctionItem(sub)) return false;

  const label = norm(sub.label);
  const codigo = norm(sub.codigo || "");

  return (
    codigo === "estoque" ||
    label === "estoque" ||
    codigo.includes("realizar-movimentacao") ||
    label.includes("realizar movimentacao") ||
    label.includes("realizar movimentação") ||
    label.includes("registrar") ||
    label.includes("adicionar") ||
    codigo.includes("produtos") ||
    label.includes("produtos") ||
    codigo.includes("entradas") ||
    label.includes("entradas") ||
    codigo.includes("saidas") ||
    label.includes("saidas") ||
    label.includes("saídas") ||
    codigo.includes("standby") ||
    label.includes("standby")
  );
};

const limitFriendlySubitems = (subs: SubItem[]) => {
  const renderable = getRenderableSubitems(subs);

  const filtered = renderable.filter(shouldShowSubitem).sort((a, b) => {
    const pa = getSubitemPriority(a);
    const pb = getSubitemPriority(b);

    if (pa !== pb) return pa - pb;

    return String(a.label || "").localeCompare(String(b.label || ""), "pt-BR");
  });

  return filtered.slice(0, 5);
};

const mergeDepartments = (departments: Department[]): Department[] => {
  const depMap = new Map<string, Department>();

  for (const dept of departments) {
    const depKey = norm(getDeptRouteKey(dept) || getDeptPermKey(dept));
    if (!depKey) continue;

    if (!depMap.has(depKey)) {
      depMap.set(depKey, {
        ...dept,
        items: [],
      });
    }

    const bucket = depMap.get(depKey)!;
    const itemMap = new Map<string, MainItem>();

    for (const existing of bucket.items) {
      itemMap.set(
        String(existing.id ?? existing.codigo ?? existing.label).toLowerCase(),
        existing
      );
    }

    for (const item of dept.items || []) {
      const itemKey = String(
        item.id ?? item.codigo ?? item.label
      ).toLowerCase();

      if (!itemMap.has(itemKey)) {
        itemMap.set(itemKey, item);
      }
    }

    bucket.items = Array.from(itemMap.values());
  }

  return Array.from(depMap.values()).sort((a, b) => {
    const ao = Number(a.ordem ?? 9999);
    const bo = Number(b.ordem ?? 9999);

    if (ao !== bo) return ao - bo;

    return String(a.name || "").localeCompare(String(b.name || ""), "pt-BR");
  });
};

const departmentKey = (dept: Department) =>
  norm(getDeptRouteKey(dept) || getDeptPermKey(dept) || dept.name || "");

const permissionHasExplicitRestriction = (
  permissions?: ModulePermissions | null
) => {
  if (!permissions || typeof permissions !== "object") return false;

  return (
    permissions.canView !== null ||
    permissions.canCreate !== null ||
    permissions.canUpdate !== null ||
    permissions.canDelete !== null ||
    permissions.canReport !== null
  );
};

const departmentHasExplicitPermission = (dept: Department) => {
  return (dept.items || []).some((item) =>
    permissionHasExplicitRestriction(item.permissions)
  );
};

const cloneDepartmentWithItems = (
  dept: Department,
  items: MainItem[]
): Department => ({
  ...dept,
  items: Array.isArray(items) ? items : [],
});

const mergeDepartmentItems = (
  primaryItems: MainItem[],
  fallbackItems: MainItem[]
): MainItem[] => {
  const itemMap = new Map<string, MainItem>();

  for (const item of primaryItems || []) {
    const key = String(item.id ?? item.codigo ?? item.label).toLowerCase();
    if (key) itemMap.set(key, item);
  }

  for (const item of fallbackItems || []) {
    const key = String(item.id ?? item.codigo ?? item.label).toLowerCase();
    if (key && !itemMap.has(key)) {
      itemMap.set(key, item);
    }
  }

  return Array.from(itemMap.values());
};

const mergeSpecificAddressDepartmentsWithFallbacks = (
  addressDepartments: Department[],
  fallbackDepartments: Department[]
): Department[] => {
  const resultMap = new Map<string, Department>();

  for (const dept of addressDepartments || []) {
    const key = departmentKey(dept);
    if (!key) continue;

    resultMap.set(key, cloneDepartmentWithItems(dept, dept.items || []));
  }

  for (const fallbackDept of fallbackDepartments || []) {
    const key = departmentKey(fallbackDept);
    if (!key) continue;

    const currentDept = resultMap.get(key);

    if (!currentDept) {
      resultMap.set(
        key,
        cloneDepartmentWithItems(fallbackDept, fallbackDept.items || [])
      );
      continue;
    }

    const currentItems = Array.isArray(currentDept.items)
      ? currentDept.items
      : [];

    const fallbackItems = Array.isArray(fallbackDept.items)
      ? fallbackDept.items
      : [];

    const currentDeptHasExplicitPermission =
      departmentHasExplicitPermission(currentDept);

    if (currentDeptHasExplicitPermission) {
      continue;
    }

    if (currentItems.length === 0 && fallbackItems.length > 0) {
      resultMap.set(key, {
        ...currentDept,
        items: fallbackItems,
        themeColor: currentDept.themeColor || fallbackDept.themeColor,
        iconKey: currentDept.iconKey || fallbackDept.iconKey,
      });
      continue;
    }

    if (currentItems.length > 0 && fallbackItems.length > 0) {
      resultMap.set(key, {
        ...currentDept,
        items: mergeDepartmentItems(currentItems, fallbackItems),
        themeColor: currentDept.themeColor || fallbackDept.themeColor,
        iconKey: currentDept.iconKey || fallbackDept.iconKey,
      });
    }
  }

  return Array.from(resultMap.values()).sort((a, b) => {
    const ao = Number(a.ordem ?? 9999);
    const bo = Number(b.ordem ?? 9999);

    if (ao !== bo) return ao - bo;

    return String(a.name || "").localeCompare(String(b.name || ""), "pt-BR");
  });
};

const ChooseAction: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessao } = useAuth();

  const [svgMarkup, setSvgMarkup] = useState<string>("");
  const [isLeaving, setIsLeaving] = useState(false);
  const [selectedAddressValue, setSelectedAddressValue] = useState<string>("");
  const [addressDropdownOpen, setAddressDropdownOpen] = useState(false);

  const departamentoSessao = String(sessao?.departamento || "");
  const isDev = norm(departamentoSessao) === "desenvolvedor";

  const sessaoEnderecos = useMemo<SessaoEndereco[]>(() => {
    const value = sessao?.enderecos_disponiveis;
    return Array.isArray(value) ? (value as SessaoEndereco[]) : [];
  }, [sessao?.enderecos_disponiveis]);

  const addressOptions = useMemo<AddressOption[]>(() => {
    const mapped = sessaoEnderecos.map(mapAddressOption);

    return [
      {
        value: ALL_ADDRESSES_VALUE,
        label: "Todos os endereços",
        lines: ["Todos os endereços"],
        isAll: true,
      },
      ...mapped,
    ];
  }, [sessaoEnderecos]);

  const defaultAddressValue = useMemo(() => {
    if (
      sessao?.endereco_selecionado_id !== undefined &&
      sessao?.endereco_selecionado_id !== null
    ) {
      return String(sessao.endereco_selecionado_id);
    }

    const firstRealAddress = addressOptions.find((opt) => !opt.isAll);
    return firstRealAddress?.value || ALL_ADDRESSES_VALUE;
  }, [sessao?.endereco_selecionado_id, addressOptions]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY_SELECTED_ADDRESS);
      if (!raw) return;

      const parsed = JSON.parse(raw);

      if (typeof parsed === "string" && parsed.trim()) {
        setSelectedAddressValue(parsed);
      }
    } catch {
      // storage inválido: ignora
    }
  }, []);

  useEffect(() => {
    if (!selectedAddressValue) {
      setSelectedAddressValue(defaultAddressValue || ALL_ADDRESSES_VALUE);
    }
  }, [defaultAddressValue, selectedAddressValue]);

  useEffect(() => {
    const selectedExists = addressOptions.some(
      (opt) => opt.value === selectedAddressValue
    );

    if (!selectedExists && addressOptions.length > 0) {
      setSelectedAddressValue(defaultAddressValue || ALL_ADDRESSES_VALUE);
    }
  }, [addressOptions, defaultAddressValue, selectedAddressValue]);

  useEffect(() => {
    try {
      if (!selectedAddressValue) return;

      window.localStorage.setItem(
        STORAGE_KEY_SELECTED_ADDRESS,
        JSON.stringify(selectedAddressValue)
      );
    } catch {
      // storage indisponível: ignora
    }
  }, [selectedAddressValue]);

  const selectedAddressOption = useMemo(() => {
    return (
      addressOptions.find((opt) => opt.value === selectedAddressValue) || null
    );
  }, [addressOptions, selectedAddressValue]);

  const baseDepartments = useMemo<Department[]>(() => {
    const value = sessao?.departamentos_acessiveis;
    return Array.isArray(value) ? value.map(mapDepartmentFromApi) : [];
  }, [sessao?.departamentos_acessiveis]);

  const departmentsByAddress = useMemo(() => {
    const map = new Map<string, Department[]>();

    const rows = Array.isArray(sessao?.enderecos_com_modulos)
      ? (sessao?.enderecos_com_modulos as SessaoEnderecoComModulos[])
      : [];

    for (const row of rows) {
      const key = String(row?.id ?? "");
      const deps = Array.isArray(row?.departamentos_acessiveis)
        ? row.departamentos_acessiveis.map(mapDepartmentFromApi)
        : [];

      map.set(key, deps);
    }

    return map;
  }, [sessao?.enderecos_com_modulos]);

  const departmentsFromAllAddresses = useMemo<Department[]>(() => {
    const allAddressDepartments = Array.from(
      departmentsByAddress.values()
    ).flat();

    if (allAddressDepartments.length > 0) {
      return mergeDepartments(allAddressDepartments);
    }

    return [];
  }, [departmentsByAddress]);

  const departments = useMemo<Department[]>(() => {
    const fallbackDepartments = mergeDepartments([
      ...departmentsFromAllAddresses,
      ...baseDepartments,
    ]);

    if (selectedAddressValue && selectedAddressValue !== ALL_ADDRESSES_VALUE) {
      const addressDepartments =
        departmentsByAddress.get(String(selectedAddressValue)) || [];

      return mergeSpecificAddressDepartmentsWithFallbacks(
        addressDepartments,
        fallbackDepartments
      );
    }

    if (departmentsFromAllAddresses.length > 0) {
      return mergeSpecificAddressDepartmentsWithFallbacks(
        departmentsFromAllAddresses,
        baseDepartments
      );
    }

    return baseDepartments;
  }, [
    selectedAddressValue,
    departmentsByAddress,
    departmentsFromAllAddresses,
    baseDepartments,
  ]);

  const currentDepartment = useMemo<Department | null>(() => {
    const byPath = findDepartmentFromPath(location.pathname, departments);
    if (byPath) return byPath;

    if (departamentoSessao) {
      const bySession = departments.find(
        (d) =>
          norm(getDeptRouteKey(d)) === norm(departamentoSessao) ||
          norm(getDeptPermKey(d)) === norm(departamentoSessao) ||
          norm(d.name) === norm(departamentoSessao)
      );

      if (bySession) return bySession;
    }

    return departments[0] || null;
  }, [location.pathname, departments, departamentoSessao]);

  const accentColor = currentDepartment?.themeColor || "#0f333b";

  const items = useMemo(() => {
    if (!currentDepartment) return [];

    return Array.isArray(currentDepartment.items)
      ? currentDepartment.items.filter((item) => {
          if (item.permissions?.canView === false) return false;
          if (!isDev && item.devOnly === true) return false;
          return true;
        })
      : [];
  }, [currentDepartment, isDev]);

  useEffect(() => {
    let active = true;

    const loadSvg = async () => {
      try {
        const response = await fetch(CharacterSvgUrl);
        const raw = await response.text();

        const recolored = raw
          .replace(/#6c63ff/gi, accentColor)
          .replace(/#d6d6e3/gi, "rgba(0,0,0,0.08)");

        if (active) {
          setSvgMarkup(recolored);
        }
      } catch {
        if (active) {
          setSvgMarkup("");
        }
      }
    };

    loadSvg();

    return () => {
      active = false;
    };
  }, [accentColor]);

  const goToWithTransition = (path: string) => {
    if (isLeaving) return;

    setIsLeaving(true);

    window.setTimeout(() => {
      navigate(path);
    }, 180);
  };

  if (!sessao) return null;

  if (!currentDepartment) {
    return (
      <div
        style={{
          width: "100%",
          minHeight: "calc(100vh - 56px)",
          background: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px 20px",
          boxSizing: "border-box",
          fontFamily: "'Inter', 'Poppins', sans-serif",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 560,
            border: "1px solid rgba(15,23,42,0.08)",
            borderRadius: 16,
            background: "#ffffff",
            boxShadow: "0 18px 42px rgba(15,23,42,0.06)",
            padding: 24,
            textAlign: "center",
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: 20,
              color: "#111827",
              fontWeight: 750,
            }}
          >
            Nenhum módulo disponível
          </h1>

          <p
            style={{
              margin: "10px 0 0",
              fontSize: 13,
              color: "#64748B",
              lineHeight: 1.5,
            }}
          >
            Não há módulos liberados para o seu usuário no endereço selecionado.
          </p>
        </div>
      </div>
    );
  }

  const activeDepartment: Department = currentDepartment;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        minHeight: "calc(100vh - 56px)",
        background:
          "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 54%, #F3F6F8 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        opacity: isLeaving ? 0 : 1,
        transform: isLeaving ? "translateY(6px)" : "translateY(0)",
        transition: "opacity 180ms ease, transform 180ms ease",
        fontFamily: "'Inter', 'Poppins', sans-serif",
      }}
      onClick={() => setAddressDropdownOpen(false)}
    >
      <div
        style={{
          position: "absolute",
          top: 80,
          right: 28,
          zIndex: 30,
          width: 286,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => setAddressDropdownOpen((prev) => !prev)}
          style={{
            width: "100%",
            minHeight: 42,
            border: "1px solid rgba(15,23,42,0.10)",
            borderRadius: 10,
            background: "#FFFFFF",
            boxShadow: "0 8px 22px rgba(15,23,42,0.05)",
            padding: "7px 10px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            textAlign: "left",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              minWidth: 0,
            }}
          >
            <MapPin size={15} color={accentColor} />

            <span
              style={{
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: "#64748B",
                }}
              >
                Endereço
              </span>

              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#111827",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 220,
                }}
              >
                {selectedAddressOption?.lines?.[0] || "Selecionar"}
              </span>
            </span>
          </span>

          <ChevronDown
            size={15}
            color="#64748B"
            style={{
              transform: addressDropdownOpen
                ? "rotate(180deg)"
                : "rotate(0deg)",
              transition: "transform 160ms ease",
              flexShrink: 0,
            }}
          />
        </button>

        {addressDropdownOpen && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 8px)",
              right: 0,
              width: "100%",
              maxHeight: 260,
              overflowY: "auto",
              background: "#FFFFFF",
              border: "1px solid rgba(15,23,42,0.08)",
              borderRadius: 12,
              boxShadow: "0 18px 38px rgba(15,23,42,0.12)",
              padding: 6,
              zIndex: 40,
            }}
          >
            {addressOptions.map((option) => {
              const selected = option.value === selectedAddressValue;

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setSelectedAddressValue(option.value);
                    setAddressDropdownOpen(false);
                  }}
                  style={{
                    width: "100%",
                    border: "none",
                    background: selected ? `${accentColor}12` : "transparent",
                    borderRadius: 10,
                    padding: "8px 10px",
                    margin: 0,
                    cursor: "pointer",
                    textAlign: "left",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {option.lines.map((line, index) => (
                    <span
                      key={`${option.value}-${index}`}
                      style={{
                        color: index === 0 ? "#111827" : "#64748B",
                        fontSize: index === 0 ? 12.2 : 11,
                        fontWeight: index === 0 ? 700 : 500,
                        lineHeight: 1.2,
                      }}
                    >
                      {line}
                    </span>
                  ))}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div
        style={{
          width: "100%",
          maxWidth: 1120,
          display: "grid",
          gridTemplateColumns: "120px minmax(0, 1fr)",
          alignItems: "center",
          gap: 250,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: 0.95,
          }}
        >
          {svgMarkup ? (
            <div
              style={{
                width: "100%",
                maxWidth: 72,
                lineHeight: 0,
                filter: isLeaving ? "blur(1px)" : "none",
                transform: isLeaving ? "scale(0.985)" : "scale(1)",
                transition: "filter 180ms ease, transform 180ms ease",
              }}
              dangerouslySetInnerHTML={{ __html: svgMarkup }}
            />
          ) : (
            <img
              src={CharacterSvgUrl}
              alt="Escolher ação"
              draggable={false}
              style={{
                width: "100%",
                maxWidth: 72,
                height: "auto",
                objectFit: "contain",
                display: "block",
                userSelect: "none",
              }}
            />
          )}
        </div>

        <div
          style={{
            minWidth: 0,
            opacity: isLeaving ? 0.92 : 1,
            transform: isLeaving ? "translateX(4px)" : "translateX(0)",
            transition: "opacity 180ms ease, transform 180ms ease",
          }}
        >
          <div
            style={{
              marginBottom: 18,
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: 8,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 750,
                  color: accentColor,
                  marginBottom: 6,
                  letterSpacing: "0.02em",
                }}
              >
                {activeDepartment.name}
              </div>

              <h1
                style={{
                  margin: 0,
                  fontSize: 24,
                  lineHeight: 1.15,
                  fontWeight: 750,
                  color: "#111827",
                  fontFamily: "'Poppins', 'Inter', sans-serif",
                }}
              >
                Qual módulo você gostaria de acessar?
              </h1>

              <p
                style={{
                  margin: "8px 0 0",
                  fontSize: 13,
                  color: "#64748B",
                  lineHeight: 1.45,
                }}
              >
                Os módulos abaixo respeitam as permissões do usuário, endereço e
                departamento.
              </p>
            </div>
          </div>

          {items.length === 0 ? (
            <div
              style={{
                border: "1px solid rgba(15,23,42,0.08)",
                borderRadius: 16,
                background: "#FFFFFF",
                padding: 22,
                color: "#64748B",
                fontSize: 13,
                lineHeight: 1.5,
                boxShadow: "0 8px 24px rgba(15,23,42,0.04)",
              }}
            >
              Nenhum módulo liberado para este departamento no endereço
              selecionado.
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gap: 10,
              }}
            >
              {items.map((item, index) => {
                const friendlySubs = limitFriendlySubitems(item.subitems || []);
                const mainPath = getMainPath(activeDepartment, item);
                const itemAccent = item.themeColor || accentColor;

                return (
                  <div
                    key={`${item.id ?? item.codigo ?? item.label}-${index}`}
                    style={{
                      border: `1px solid ${itemAccent}20`,
                      borderRadius: 14,
                      background: "#FFFFFF",
                      padding: 14,
                      boxShadow: "0 4px 14px rgba(15,23,42,0.035)",
                      transition:
                        "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow =
                        "0 10px 22px rgba(15,23,42,0.06)";
                      e.currentTarget.style.borderColor = `${itemAccent}42`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 14px rgba(15,23,42,0.035)";
                      e.currentTarget.style.borderColor = `${itemAccent}20`;
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => goToWithTransition(mainPath)}
                      style={{
                        width: "100%",
                        background: "transparent",
                        border: "none",
                        padding: 0,
                        margin: 0,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        textAlign: "left",
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 7,
                            marginBottom: 3,
                          }}
                        >
                          <ChevronRight size={14} color={itemAccent} />

                          <span
                            style={{
                              fontSize: 15,
                              fontWeight: 750,
                              color: "#111827",
                              lineHeight: 1.2,
                            }}
                          >
                            {item.label}
                          </span>
                        </div>

                        <div
                          style={{
                            fontSize: 12,
                            color: "#6B7280",
                            paddingLeft: 21,
                            lineHeight: 1.2,
                          }}
                        >
                          Abrir módulo
                        </div>
                      </div>

                      <ArrowRight size={16} color={itemAccent} />
                    </button>

                    {friendlySubs.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 8,
                          marginTop: 12,
                          paddingLeft: 21,
                        }}
                      >
                        {friendlySubs.map((sub, subIndex) => (
                          <button
                            key={`${sub.label}-${sub.codigo ?? subIndex}`}
                            type="button"
                            onClick={() =>
                              goToWithTransition(
                                buildSubPath(activeDepartment, item, sub)
                              )
                            }
                            style={{
                              border: `1px solid ${itemAccent}20`,
                              background: `${itemAccent}0D`,
                              color: itemAccent,
                              borderRadius: 999,
                              padding: "7px 11px",
                              fontSize: 11.5,
                              fontWeight: 700,
                              cursor: "pointer",
                              lineHeight: 1.15,
                              transition:
                                "transform 140ms ease, background 140ms ease, border-color 140ms ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform =
                                "translateY(-1px)";
                              e.currentTarget.style.background = `${itemAccent}16`;
                              e.currentTarget.style.borderColor = `${itemAccent}42`;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.background = `${itemAccent}0D`;
                              e.currentTarget.style.borderColor = `${itemAccent}20`;
                            }}
                          >
                            {sub.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChooseAction;
